import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineSpinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Library, Plus, Pencil, GitCompare, ArrowUpCircle, CheckCircle2, Layers, Search } from "lucide-react";

const SCOPE_CLS = { system: "bg-indigo-50 text-indigo-700 border-indigo-200", account: "bg-sky-50 text-sky-700 border-sky-200", group: "bg-teal-50 text-teal-700 border-teal-200" };
const STATUS_CLS = { published: "bg-emerald-50 text-emerald-700 border-emerald-200", draft: "bg-slate-100 text-slate-600 border-slate-200", needs_review: "bg-amber-50 text-amber-700 border-amber-200", archived: "bg-slate-100 text-slate-500 border-slate-200" };
const BLANK = { title: "", content_key: "", purpose: "support", category: "", subcategory: "", applicable_state: "ALL", program: "", content: "", cta: "", scope: "account", organization_id: "", status: "draft" };

export default function AnswerLibrary() {
  const { ctx } = useAuth();
  const [items, setItems] = useState([]);
  const [updates, setUpdates] = useState({});
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [compare, setCompare] = useState(null);
  const [effKey, setEffKey] = useState("");
  const [effOrg, setEffOrg] = useState("");
  const [effRes, setEffRes] = useState(null);
  const [f, setF] = useState({ scope: "all", purpose: "all", state: "", program: "", category: "all", status: "all", updates: false, q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, u, m] = await Promise.all([
        api.get("/copilot/answers"), api.get("/copilot/answers/updates"), api.get("/copilot/meta"),
      ]);
      setItems(l.data.items || []);
      const map = {}; (u.data.items || []).forEach((x) => { map[x.override.id] = x; });
      setUpdates(map);
      setMeta(m.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, path, body) => {
    try { await api.post(`/copilot/answers/${id}/${path}`, body || {}); toast.success("Done"); setCompare(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resolveEffective = async () => {
    if (!effKey) return;
    try {
      const qs = effOrg ? `&organization_id=${effOrg}` : "";
      const { data } = await api.get(`/copilot/answers/effective?content_key=${encodeURIComponent(effKey)}${qs}`);
      setEffRes(data);
    } catch (e) { setEffRes(null); toast.error(apiError(e)); }
  };

  const filtered = items.filter((a) => {
    if (f.scope !== "all" && a.scope !== f.scope) return false;
    if (f.purpose !== "all" && a.purpose !== f.purpose) return false;
    if (f.state && (a.applicable_state || "").toUpperCase() !== f.state.toUpperCase()) return false;
    if (f.program && !(a.program || "").toLowerCase().includes(f.program.toLowerCase())) return false;
    if (f.category !== "all" && a.category !== f.category) return false;
    if (f.status !== "all" && a.status !== f.status) return false;
    if (f.updates && !updates[a.id]) return false;
    if (f.q && !`${a.title} ${a.content_key} ${a.content}`.toLowerCase().includes(f.q.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="py-16 flex justify-center"><InlineSpinner /></div>;

  return (
    <div data-testid="answer-library">
      <PageHeader title="Answer Library" description="Trusted communication content with System → Account → Group inheritance. System updates never overwrite your customizations."
        actions={<Button className="bg-[#006090] hover:bg-[#004E75]" onClick={() => setEditing({ ...BLANK })} data-testid="al-add"><Plus className="w-4 h-4 mr-1.5" />New Answer Block</Button>} />

      {/* Effective Content resolver */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5" data-testid="al-effective">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" />Effective Content</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div><Label className="text-xs">Content key</Label><Input value={effKey} onChange={(e) => setEffKey(e.target.value)} placeholder="licensing.ca.pc.getting_started" className="h-9 mt-1 w-72" data-testid="al-eff-key" /></div>
          <div><Label className="text-xs">District (org id, optional)</Label>
            <Select value={effOrg || "none"} onValueChange={(v) => setEffOrg(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9 mt-1 w-56" data-testid="al-eff-org"><SelectValue placeholder="Account-wide" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="none">Account-wide</SelectItem>{(meta?.organizations || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
          <Button variant="outline" onClick={resolveEffective} data-testid="al-eff-resolve">Resolve</Button>
        </div>
        {effRes && (
          <div className="mt-3 rounded-lg bg-slate-50 border p-3 text-sm" data-testid="al-eff-result">
            <p>Resolved from <b className="text-[#006090]">{effRes.resolved_from}</b>{effRes.organization_id ? ` for ${effRes.organization_id}` : " (account-wide)"}.
              {" "}System update: <b>{effRes.update_available ? "Available" : "Current"}</b></p>
            <p className="mt-1 text-slate-600 whitespace-pre-wrap">{effRes.effective?.content}</p>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search title, key, content…" className="pl-9" data-testid="al-search" /></div>
        <FSel v={f.scope} on={(v) => setF({ ...f, scope: v })} all="All scopes" opts={[["system", "System"], ["account", "Account"], ["group", "Group"]]} tid="al-filter-scope" />
        <FSel v={f.purpose} on={(v) => setF({ ...f, purpose: v })} all="All purposes" opts={[["recruiting", "Recruiting"], ["support", "Support"]]} tid="al-filter-purpose" />
        <FSel v={f.category} on={(v) => setF({ ...f, category: v })} all="All categories" opts={[...new Set(items.map((i) => i.category).filter(Boolean))].map((c) => [c, c])} tid="al-filter-category" />
        <FSel v={f.status} on={(v) => setF({ ...f, status: v })} all="All statuses" opts={[["published", "Published"], ["draft", "Draft"], ["needs_review", "Needs Review"], ["archived", "Archived"]]} tid="al-filter-status" />
        <Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase().slice(0, 3) })} placeholder="State" className="h-9 w-24" data-testid="al-filter-state" />
        <Button size="sm" variant={f.updates ? "default" : "outline"} className={f.updates ? "bg-amber-500" : ""} onClick={() => setF({ ...f, updates: !f.updates })} data-testid="al-filter-updates">Update Available</Button>
      </div>

      {filtered.length === 0 ? <EmptyState icon={Library} title="No answer blocks" description="Create your first answer block or adjust filters." /> : (
        <div className="space-y-2" data-testid="al-list">
          {filtered.map((a) => {
            const up = updates[a.id];
            return (
              <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`al-row-${a.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-slate-800">{a.title || a.content_key || "Untitled"}</span>
                  <Badge variant="outline" className={SCOPE_CLS[a.scope]}>{a.scope}</Badge>
                  <Badge variant="outline" className={STATUS_CLS[a.status]}>{a.status}</Badge>
                  {a.applicable_state && <Badge variant="outline">{a.applicable_state}</Badge>}
                  {a.content_key && <Badge variant="outline" className="font-mono text-[10px]">{a.content_key}</Badge>}
                  <span className="text-xs text-slate-400">v{a.version || 1}</span>
                  {up && <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`al-update-${a.id}`}>SYSTEM UPDATE AVAILABLE</Badge>}
                </div>
                {a.content && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{a.content}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  {a.can_manage !== false && <Button size="sm" variant="outline" onClick={() => setEditing(a)} data-testid={`al-edit-${a.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>}
                  {up && <>
                    <Button size="sm" variant="outline" onClick={async () => { const { data } = await api.get(`/copilot/answers/${a.id}/compare`); setCompare({ row: a, data }); }} data-testid={`al-compare-${a.id}`}><GitCompare className="w-3.5 h-3.5 mr-1" />Compare</Button>
                    <Button size="sm" variant="outline" onClick={() => act(a.id, "keep")} data-testid={`al-keep-${a.id}`}>Keep My Version</Button>
                    <Button size="sm" variant="outline" onClick={() => act(a.id, "adopt-system")} data-testid={`al-adopt-${a.id}`}><ArrowUpCircle className="w-3.5 h-3.5 mr-1" />Adopt System</Button>
                  </>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <AnswerDialog meta={meta} value={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {compare && <CompareDialog payload={compare} onClose={() => setCompare(null)}
        onUpdate={(content) => act(compare.row.id, "update-my-version", { content })} onAdopt={() => act(compare.row.id, "adopt-system")} />}
    </div>
  );
}

const FSel = ({ v, on, all, opts, tid }) => (
  <Select value={v} onValueChange={on}><SelectTrigger className="h-9 w-40" data-testid={tid}><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white"><SelectItem value="all">{all}</SelectItem>{opts.map(([val, l]) => <SelectItem key={val} value={val}>{l}</SelectItem>)}</SelectContent></Select>
);

function AnswerDialog({ meta, value, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK, ...value });
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setForm((s) => ({ ...s, [k]: val }));
  const isNew = !value.id;
  const save = async () => {
    if (!form.content?.trim()) { toast.error("Content is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.organization_id) delete payload.organization_id;
      if (isNew) await api.post("/copilot/answers", payload);
      else await api.patch(`/copilot/answers/${value.id}`, payload);
      toast.success(isNew ? "Created" : "Saved"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg max-h-[88vh] overflow-y-auto" data-testid="al-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New Answer Block" : "Edit Answer Block"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="al-f-title" /></div>
          <div><Label className="text-xs">Stable content key</Label><Input value={form.content_key} onChange={(e) => set("content_key", e.target.value)} placeholder="licensing.ca.pc.getting_started" className="font-mono" data-testid="al-f-key" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Scope</Label>
              <Select value={form.scope} onValueChange={(v) => set("scope", v)}><SelectTrigger className="h-9" data-testid="al-f-scope"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="account">Account</SelectItem><SelectItem value="group">Group (District)</SelectItem><SelectItem value="system">System</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs">Purpose</Label>
              <Select value={form.purpose} onValueChange={(v) => set("purpose", v)}><SelectTrigger className="h-9" data-testid="al-f-purpose"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="recruiting">Recruiting</SelectItem><SelectItem value="support">Support</SelectItem></SelectContent></Select></div>
          </div>
          {form.scope === "group" && (
            <div><Label className="text-xs">District</Label>
              <Select value={form.organization_id || "none"} onValueChange={(v) => set("organization_id", v === "none" ? "" : v)}>
                <SelectTrigger className="h-9" data-testid="al-f-org"><SelectValue placeholder="Select district" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{(meta?.organizations || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">State</Label><Input value={form.applicable_state} onChange={(e) => set("applicable_state", e.target.value.toUpperCase().slice(0, 3))} data-testid="al-f-state" /></div>
            <div><Label className="text-xs">Category</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="al-f-category" /></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger className="h-9" data-testid="al-f-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="needs_review">Needs Review</SelectItem></SelectContent></Select></div>
          </div>
          <div><Label className="text-xs">Content</Label><Textarea rows={5} value={form.content} onChange={(e) => set("content", e.target.value)} data-testid="al-f-content" /></div>
          <div><Label className="text-xs">Call to action (optional)</Label><Input value={form.cta} onChange={(e) => set("cta", e.target.value)} data-testid="al-f-cta" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={saving} onClick={save} data-testid="al-save">{saving ? <InlineSpinner /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompareDialog({ payload, onClose, onUpdate, onAdopt }) {
  const { data } = payload;
  const [draft, setDraft] = useState(payload.row.content || "");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="al-compare-dialog">
        <DialogHeader><DialogTitle>Compare — Current {data.current?.scope} vs {data.parent_scope} parent</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {(data.differences || []).map((d) => (
            <div key={d.field} className={`rounded-lg border p-3 ${d.changed ? "border-amber-300 bg-amber-50" : "border-slate-200"}`} data-testid={`al-diff-${d.field}`}>
              <p className="text-[11px] font-semibold uppercase text-slate-400">{d.field}{d.changed && <span className="text-amber-600"> · differs</span>}</p>
              <div className="grid grid-cols-2 gap-3 mt-1 text-sm">
                <div><p className="text-[10px] text-slate-400">Current (yours)</p><p className="text-slate-700 whitespace-pre-wrap">{String(d.current ?? "—")}</p></div>
                <div><p className="text-[10px] text-slate-400">New parent</p><p className="text-slate-700 whitespace-pre-wrap">{String(d.parent ?? "—")}</p></div>
              </div>
            </div>
          ))}
          <div className="rounded-lg border border-[#006090]/30 bg-[#006090]/5 p-3">
            <p className="text-xs font-semibold text-[#006090] mb-1">Update My Version (edit using the new parent as reference — no auto-merge)</p>
            <Textarea rows={5} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="al-compare-draft" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={onAdopt} data-testid="al-compare-adopt"><CheckCircle2 className="w-4 h-4 mr-1" />Adopt System Version</Button>
          <Button className="bg-[#006090] hover:bg-[#004E75]" onClick={() => onUpdate(draft)} data-testid="al-compare-update">Update My Version</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
